import PropTypes from 'prop-types';
import {
	Box,
	FormControlLabel,
	Paper,
	Stack,
	Switch,
	Tooltip,
	Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

const AutomaticEmailSync = ({
	emailSyncCadenceValue,
	emailSyncCadenceUnit,
	hasMicrosoftProvider,
	hasGoogleProvider,
	microsoftEnabled,
	onMicrosoftEnabledChange,
	microsoftSyncStart,
	onMicrosoftSyncStartChange,
	microsoftSyncStartMin,
	microsoftInputRef,
	shouldDisableMicrosoftDatePicker,
	gmailEnabled,
	onGmailEnabledChange,
	gmailSyncStart,
	onGmailSyncStartChange,
	googleSyncStartMin,
	gmailInputRef,
	shouldDisableGmailDatePicker,
	isRunning,
}) => {
	return (
		<Box sx={{ mb: 2 }}>
			<Typography
				variant='subtitle1'
				sx={{
					fontWeight: 600,
					fontSize: '0.9rem',
					textTransform: 'uppercase',
					letterSpacing: '0.5px',
				}}
				gutterBottom
			>
				Automatic Email Sync
			</Typography>

			<Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
				Select which email providers to automatically sync every{' '}
				{emailSyncCadenceValue} {emailSyncCadenceUnit}.
			</Typography>

			<Stack spacing={1.5}>
				{hasMicrosoftProvider && (
					<Paper variant='outlined' sx={{ p: 1.5 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<Tooltip
								title='Unable to edit or enable syncs while a sync is currently running.'
								placement='top'
								disableHoverListener={!isRunning}
								arrow
							>
								<FormControlLabel
									control={
										<Switch
											checked={microsoftEnabled}
											onChange={onMicrosoftEnabledChange}
											color='primary'
											disabled={isRunning}
											slotProps={{
												input: {
													'data-testid': 'microsoft-sync-switch',
												},
											}}
										/>
									}
									label=''
									sx={{ m: 0 }}
								/>
							</Tooltip>

							<Typography
								variant='body2'
								sx={{ fontWeight: 500, minWidth: 'fit-content' }}
							>
								Microsoft
							</Typography>

							{microsoftEnabled && (
								<Tooltip
									title="This is used by the next automatic job if it's after last synced job or no earlier than 7 days ago for the first job."
									placement='top'
									arrow
								>
									<Box sx={{ ml: 'auto', width: 240 }}>
										<DateTimePicker
											inputRef={microsoftInputRef}
											value={microsoftSyncStart}
											label='Start Sync From'
											slotProps={{
												textField: {
													size: 'small',
													fullWidth: true,
													error: false,
												},
											}}
											onChange={onMicrosoftSyncStartChange}
											timeSteps={{ minutes: 1 }}
											minDateTime={microsoftSyncStartMin}
											disableFuture
											disabled={isRunning || shouldDisableMicrosoftDatePicker}
											format='MM/DD/YYYY hh:mm A'
										/>
									</Box>
								</Tooltip>
							)}
						</Box>
					</Paper>
				)}

				{hasGoogleProvider && (
					<Paper variant='outlined' sx={{ p: 1.5 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<Tooltip
								title='Unable to edit or enable syncs while a sync is currently running.'
								placement='top'
								disableHoverListener={!isRunning}
								arrow
							>
								<FormControlLabel
									control={
										<Switch
											checked={gmailEnabled}
											onChange={onGmailEnabledChange}
											color='primary'
											disabled={isRunning}
											slotProps={{
												input: {
													'data-testid': 'gmail-sync-switch',
												},
											}}
										/>
									}
									label=''
									sx={{ m: 0 }}
								/>
							</Tooltip>

							<Typography
								variant='body2'
								sx={{ fontWeight: 500, minWidth: 'fit-content' }}
							>
								Gmail
							</Typography>

							{gmailEnabled && (
								<Tooltip
									title="This is used by the next automatic job if it's after last synced job or no earlier than 7 days ago for the first job."
									placement='top'
									arrow
								>
									<Box sx={{ ml: 'auto', width: 240 }}>
										<DateTimePicker
											inputRef={gmailInputRef}
											value={gmailSyncStart}
											label='Start Sync From'
											slotProps={{
												textField: {
													size: 'small',
													fullWidth: true,
													error: false,
												},
											}}
											onChange={onGmailSyncStartChange}
											timeSteps={{ minutes: 1 }}
											minDateTime={googleSyncStartMin}
											disableFuture
											disabled={isRunning || shouldDisableGmailDatePicker}
											format='MM/DD/YYYY hh:mm A'
										/>
									</Box>
								</Tooltip>
							)}
						</Box>
					</Paper>
				)}
			</Stack>
		</Box>
	);
};

AutomaticEmailSync.propTypes = {
	emailSyncCadenceValue: PropTypes.number.isRequired,
	emailSyncCadenceUnit: PropTypes.oneOf(['minutes', 'hours']).isRequired,
	hasMicrosoftProvider: PropTypes.bool.isRequired,
	hasGoogleProvider: PropTypes.bool.isRequired,

	microsoftEnabled: PropTypes.bool,
	onMicrosoftEnabledChange: PropTypes.func,
	microsoftSyncStart: PropTypes.object,
	onMicrosoftSyncStartChange: PropTypes.func,
	microsoftSyncStartMin: PropTypes.object,
	microsoftInputRef: PropTypes.shape({
		current: PropTypes.any,
	}),
	shouldDisableMicrosoftDatePicker: PropTypes.bool,

	gmailEnabled: PropTypes.bool,
	onGmailEnabledChange: PropTypes.func,
	gmailSyncStart: PropTypes.object,
	onGmailSyncStartChange: PropTypes.func,
	googleSyncStartMin: PropTypes.object,
	gmailInputRef: PropTypes.shape({
		current: PropTypes.any,
	}),
	shouldDisableGmailDatePicker: PropTypes.bool,

	isRunning: PropTypes.bool.isRequired,
};

export default AutomaticEmailSync;
